import { Injectable } from '@angular/core';
import { AccModeEnum, ApplicationConfig, ProjectInfo, SystemDetails, SystemInfo, SystemTypes } from '@frontend-site/shared/data';
import { combineLatest } from 'rxjs';
import { PermissionsService } from './permissions.service';
import { ProjectStore } from './project';
import { SessionRepository } from './session';
import { SystemStore } from './system';

declare let pendo: any;

// TODO: AI concepts (and other concepts) should be injected into this service.
//       it should not know about AI or business concepts directly.
const AI_DEFAULT_VALUE = 'Off';
const AI_ENABLEMENT_PENDO = {
    1: 'On',
    2: 'Limited',
    3: 'Off'
} as const;

function aiEnablementToAccessLevel(aiEnablement: number): string {
    return AI_ENABLEMENT_PENDO[aiEnablement as keyof typeof AI_ENABLEMENT_PENDO] ?? AI_DEFAULT_VALUE;
}

function getAiActivationStatus(aiAccessLevel: string, aiConsentRequired: boolean): string {
    return !aiConsentRequired ? AI_DEFAULT_VALUE : aiAccessLevel;
}

@Injectable({ providedIn: 'root' })
export class PendoService {
    // ToDo:: Currently not supports system mode.
    readonly MONTH_COUNT_FOR_3_M = 3;
    readonly MONTH_COUNT_FOR_6_M = 6;
    readonly DAYS_COUNT_FOR_NEW_USER = 14;
    readonly EMEA_IN_URL = 'emea.panaya.com';
    readonly US_IN_URL = 'my.panaya.com';
    readonly STAGING_IN_URL = 'staging.panaya.com';

    constructor(
        private systemStore: SystemStore,
        private sessionStore: SessionRepository,
        private projectStore: ProjectStore,
        private permissionsService: PermissionsService
    ) {}

    initPendo(appConfig: ApplicationConfig, projectDetails: ProjectInfo): void {
        if (appConfig) {
            const pendoObject = this.getPendoObject(appConfig, projectDetails);
            pendo.initialize(pendoObject);

            if (this.noLoginInLastMonth(appConfig, this.MONTH_COUNT_FOR_3_M)) {
                this.setCookie('noLoginFor3m', 'true', this.DAYS_COUNT_FOR_NEW_USER);
            }
            if (this.noLoginInLastMonth(appConfig, this.MONTH_COUNT_FOR_6_M)) {
                this.setCookie('noLoginFor6m', 'true', this.DAYS_COUNT_FOR_NEW_USER);
            }

            combineLatest([
                this.systemStore.selectedSystemId$,
                this.projectStore.projectDetails$,
                this.systemStore.systemDetails$
            ]).subscribe(([selectedSystemId, projectInfo, systemDetails]: [any, any, SystemDetails | null | undefined]) => {
                this.updatePendo(appConfig, projectInfo, systemDetails, undefined, selectedSystemId);
            });
        }
    }

    private getSystemCountByVendor(appConfig: ApplicationConfig, vendorName: string): number {
        //todo: need to create systemBean model
        const isVendor = (systemBean: any): boolean => systemBean.systemType === vendorName;
        return appConfig.systems.map((system) => (isVendor(system) ? 1 : 0)).reduce((acc: number, num: number) => acc + num, 0);
    }

    getPendoAccountId(companyId: number): string {
        return this.getRegionPrefixFromUrl() + companyId.toString();
    }

    private getRegionPrefixFromUrl(): string {
        const str = location.href.toLowerCase();
        if (str.indexOf(this.EMEA_IN_URL) > -1) {
            return 'EU:';
        } else if (str.indexOf(this.US_IN_URL) > -1) {
            return 'US:';
        } else if (str.indexOf(this.STAGING_IN_URL) > -1) {
            return 'ST:';
        } else {
            return 'QA:';
        }
    }

    private monthDiff(d1: Date, d2: Date): number {
        let months;
        months = (d2.getFullYear() - d1.getFullYear()) * 12;
        months -= d1.getMonth();
        months += d2.getMonth();
        return months <= 0 ? 0 : months;
    }

    private noLoginInLastMonth(appConfig: ApplicationConfig, monthCount: number): boolean {
        return appConfig.lastLoginTime === null || this.monthDiff(new Date(appConfig.lastLoginTime), new Date()) >= monthCount;
    }

    private setCookie(name: string, value: string, days: number): void {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/';
    }

    private getCookie(name: string): boolean | null {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1, c.length);
            }
            if (c.indexOf(nameEQ) == 0) {
                return c.substring(nameEQ.length, c.length) == 'true';
            }
        }
        return null;
    }

    private getSystemDomain(appConfig: ApplicationConfig, projectInfo?: ProjectInfo | null, systemId?: number | null): string {
        if (projectInfo?.systemType) {
            return SystemTypes[projectInfo?.systemType];
        }

        if (systemId) {
            const systemInfo = appConfig.systems?.find((s) => s.id === systemId);
            if (systemInfo) {
                return systemInfo.systemType;
            }
        }

        const systemTypes = [...new Set(appConfig.systems?.map((s) => s.systemType))];
        if (systemTypes.length === 1) {
            return systemTypes[0];
        }

        return 'MULTI';
    }

    private getPendoObject(
        appConfig: ApplicationConfig,
        projectInfo: ProjectInfo | null,
        systemDetails?: SystemDetails | null | undefined,
        isAuthenticated?: boolean,
        systemId?: number | null
    ): PendoObject {
        const systemDomain = this.getSystemDomain(appConfig, projectInfo, systemId);
        const noLoginFor3m = this.getCookie('noLoginFor3m') || this.noLoginInLastMonth(appConfig, this.MONTH_COUNT_FOR_3_M);
        const noLoginFor6m = this.getCookie('noLoginFor6m') || this.noLoginInLastMonth(appConfig, this.MONTH_COUNT_FOR_6_M);
        const isEverExtracted = !!this.projectStore.projectDetails?.activeEtlId;
        const isSalesforceAdmin = this.permissionsService.hasPermissions('Salesforce : manage');
        const isRiskAnalysisEnabled = projectInfo?.riskAnalysisEnabled;
        const isICRProject = projectInfo?.accMode === AccModeEnum.ACC_NEW;
        const aiAccessLevel = systemDetails?.aiEnablement ? aiEnablementToAccessLevel(systemDetails.aiEnablement) : AI_DEFAULT_VALUE;
        const aiConsentRequired = systemDetails?.aiConsent === 2;
        const aiActivationStatus = getAiActivationStatus(aiAccessLevel, aiConsentRequired);

        return {
            visitor: {
                id: appConfig.user.email, // Required if user is logged in
                email: appConfig.user.email,
                full_name: appConfig.user.fullName,
                firstName: appConfig.user.firstName,
                lastName: appConfig.user.lastName,
                systemDomain: systemDomain,
                sapSystemCount: this.getSystemCountByVendor(appConfig, 'SAP'),
                oracleSystemCount: this.getSystemCountByVendor(appConfig, 'ORACLE'),
                salesforceSystemCount: this.getSystemCountByVendor(appConfig, 'SALESFORCE'),
                isSalesforceAdmin,
                isEverExtracted,
                isAuthenticated: isAuthenticated,
                isSfFreeTrial: projectInfo?.sfFreeTrial,
                isOptimizeButtonEnabled: this.isRiskAnalysisEnabledAndProjectActiveByEtl(projectInfo) && this.hasOptimizeButtonPermission(),
                isRiskAnalysisEnabled,
                isICRProject,
                aiAccessLevel: aiAccessLevel,
                aiConsentRequired,
                aiActivationStatus: aiActivationStatus,
                uiLanguage: appConfig.language,
                noLoginFor3m: noLoginFor3m,
                noLoginFor6m: noLoginFor6m,
                projectId: projectInfo?.id,
                projectName: projectInfo?.projectName,
                projectType: projectInfo?.projectTypeString,
                systemId: projectInfo?.systemId ?? systemId,
                permissions: this.getUserProjectPermissions(appConfig, projectInfo?.id),
                user: appConfig.user.email,
                key: appConfig.pendoKey,
                jobTitle: appConfig.user.jobTitleKey

                // You can add any additional visitor level key-values here,
                // as long as it's not one of the above reserved names.
            },

            account: {
                id: this.getPendoAccountId(appConfig.companyId),
                name: appConfig.companyName

                // You can add any additional account level key-values here,
                // as long as it's not one of the above reserved names.
            }
        };
    }

    private isRiskAnalysisEnabledAndProjectActiveByEtl(projectInfo: ProjectInfo | null): boolean {
        return this.isRiskAnalysisEnabled(projectInfo?.riskAnalysisEnabled) && this.isProjectActiveByEtl();
    }

    private isRiskAnalysisEnabled(riskAnalysisEnabled: boolean | undefined): boolean {
        return riskAnalysisEnabled ? riskAnalysisEnabled : false;
    }

    private isProjectActiveByEtl(): boolean {
        return (
            this.projectStore.projectDetails?.activeEtlId ==
                this.sessionStore.applicationConfig?.systems.find((system: SystemInfo) => {
                    return system.id == this.projectStore.projectDetails?.systemId;
                })?.activeEtlId && this.projectStore.projectDetails?.projectStatus?.toUpperCase() !== 'INACTIVE'
        );
    }

    private hasOptimizeButtonPermission(): boolean {
        return this.permissionsService.hasPermissions('Test Cycle : edit') && this.permissionsService.hasPermissions('Test Cycle : create');
    }

    private updatePendo(
        appConfig: ApplicationConfig,
        projectInfo: ProjectInfo | null,
        systemDetails: SystemDetails | null | undefined,
        isSfAuthenticated: boolean | undefined,
        systemId?: number | null
    ): void {
        const pendoObject = this.getPendoObject(appConfig, projectInfo, systemDetails, isSfAuthenticated, systemId);
        pendo.updateOptions(pendoObject);
    }

    private getUserProjectPermissions(appConfig: ApplicationConfig, projectId?: number): string {
        const allProjectsPermissions = appConfig.permissions;
        if (!projectId || !allProjectsPermissions) {
            return '';
        }
        const projectPermissions = allProjectsPermissions[projectId];
        return projectPermissions ? projectPermissions.join() : '';
    }

    public trackPendoEvent(key: string, data: Record<keyof any, any>): void {
        try {
            pendo.track(key, data);
        } catch (e) {}
    }
}

interface PendoObject {
    visitor: VisitorMetadata;
    account: AccountMetadata;
}

interface VisitorMetadata {
    id: string;
    email: string;
    full_name: string;
    firstName: string;
    lastName: string;
    systemDomain: string;
    sapSystemCount: number;
    oracleSystemCount: number;
    salesforceSystemCount: number;
    isSalesforceAdmin: boolean;
    isEverExtracted: boolean | undefined;
    isAuthenticated: boolean | undefined;
    uiLanguage: string;
    noLoginFor3m: boolean;
    noLoginFor6m: boolean;
    projectId: number | undefined;
    projectName: string | undefined;
    projectType: string | undefined;
    systemId: number | null | undefined;
    permissions: string;
    user: string;
    key: string;
    jobTitle: string | undefined;
    isSfFreeTrial: boolean | undefined;
    isOptimizeButtonEnabled: boolean;
    isRiskAnalysisEnabled: boolean | undefined;
    isICRProject: boolean | undefined;
    aiAccessLevel: string | undefined;
    aiConsentRequired: boolean;
    aiActivationStatus: string | undefined;
}

interface AccountMetadata {
    id: string;
    name: string;
}

interface DataForPendoUpdate {
    selectedSystemId: number | null | undefined;
    projectInfo: ProjectInfo | null;
    isSfAuthenticated: boolean | undefined;
}
